import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { ArrowLeft, Briefcase, Building2, Edit, Trash2, Plus, Archive, ArchiveRestore, RefreshCw, AlertCircle } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { canUserArchiveJobRole } from "../utils/jobrole-permissions";
import { JobRole, Department, JobPosition } from "../types";
import FileAttachments from "../components/FileAttachments";
import { LoadingPageSkeleton } from "../components/common/LoadingPageSkeleton";
import { formatArchiveDate } from "../utils/helpers";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { ActivityLog } from "../components/ActivityLog";
import AccessRestricted from "../components/AccessRestricted";
import { JobPositionsTab } from "../components/JobRoles/JobPositionsTab";
import { MultiSelect } from "../components/ui/multi-select";

const getJobRole = async (id: any) => {
  return await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/jobroles/${id}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });
}

export default function JobRolePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { jobRoles, departments, users, updateJobRole, archiveJobRole, unarchiveJobRole, deleteJobRole, mapJobRole, qualificationPresets, jobGrades, fetchJobGrades } = useData();
  const { currentUser } = useAuth();
  const [jobRole, setJobRole] = useState<JobRole | null>(null);
  const [department, setDepartment] = useState<Department | undefined>(undefined);
  const [editingField, setEditingField] = useState<null | string>(null);
  const [editValue, setEditValue] = useState<string | string[]>("");
  const [editOutcomes, setEditOutcomes] = useState<string[]>([]);
  const [editDuties, setEditDuties] = useState<string[]>([]);
  const [editPreferredQualifications, setEditPreferredQualifications] = useState<string[]>([]);
  const [editRequiredCertificates, setEditRequiredCertificates] = useState<string[]>([]);
  const [editJobGradeId, setEditJobGradeId] = useState<string>("");
  const [openArchive, setOpenArchive] = useState(false);
  const [openUnarchive, setOpenUnarchive] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [activeTab, setActiveTab] = useState<'jobrole' | 'job_positions' | 'activity_log'>('jobrole');
  const [hasAccessToJobRole, setHasAccessToJobRole] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [positions, setPositions] = useState<JobPosition[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [shouldOpenPositionForm, setShouldOpenPositionForm] = useState(false);


  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobRole = async () => {
      if (id) {
        setLoading(true);
        try {
          const response = await getJobRole(id);

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.role) {
              console.log('✅ Fetched job role with attachments:', data.role);
              setJobRole(mapJobRole(data.role));
              setAttachments(data.role.attachments);
              setHasAccessToJobRole(true);
            }
          } else {
            console.error('Failed to fetch job role:', response.statusText);
            setHasAccessToJobRole(false);
          }

        } catch (error) {
          console.error('Error fetching job role:', error);
          setHasAccessToJobRole(false);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchJobRole();
  }, [id, jobRoles]); // Keep jobRoles dependency if needed, though usually id is enough

  useEffect(() => {
    setDepartment(jobRole?.department);
  }, [jobRole]);

  useEffect(() => {
    if (jobGrades.length === 0) {
      fetchJobGrades();
    }
  }, []);

  const fetchPositions = async () => {
    if (!id) return;
    setPositionsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/jobroles/${id}/positions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setPositions(data.positions);
      }
    } catch (error) {
      console.error("Error fetching positions:", error);
    } finally {
      setPositionsLoading(false);
    }
  };

  useEffect(() => {
    if (jobRole) {
      fetchPositions();
    }
  }, [jobRole?.id]);

  if (loading) {
    return <LoadingPageSkeleton />;
  }

  // Define Access Restricted Component
  const _AccessRestricted = () => (
    <div className="flex items-center justify-center h-[60vh]">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Access Restricted</h3>
          <p className="text-muted-foreground">You don't have permission to view this job role.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/job-roles')}>
            Back to Job Roles
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // If fetch failed or job role not found/accessible from backend
  if (!hasAccessToJobRole || !jobRole) {
    return <AccessRestricted backMessage="Back to Job Roles" navigateTo="/job-roles" accessMessage="You don't have permission to view this job role." />;
  }

  const canArchive = currentUser && canUserArchiveJobRole(currentUser);
  // Find department by either code or ID
  // const department = departments.find(dept => 
  //   dept.departmentCode === jobRole.departmentId || 
  //   dept.id === jobRole.departmentId
  // );
  // const department = jobRole.department;

  const archiver = jobRole.archivedBy ? (
    typeof jobRole.archivedBy === 'string'
      ? users.find(user => user.id == jobRole.archivedBy)
      : jobRole.archivedBy
  ) : null;

  const startEdit = (field: string, value: string | string[]) => {
    setEditingField(field);
    setEditValue(value);

    if (field === 'outcomes') {
      setEditOutcomes([...jobRole.outcomes]);
    } else if (field === 'keyDuties') {
      setEditDuties([...jobRole.keyDuties]);
    } else if (field === 'preferredQualifications') {
      setEditPreferredQualifications(jobRole.preferredQualifications?.map((q: any) => q.id) || []);
    } else if (field === 'requiredCertificatesAndLicenses') {
      setEditRequiredCertificates(jobRole.requiredCertificatesAndLicenses?.map((c: any) => c.id) || []);
    } else if (field === 'salaryRange') {
      setEditJobGradeId(jobRole.jobGradeId || "none");
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
    setEditOutcomes([]);
    setEditDuties([]);
    setEditPreferredQualifications([]);
    setEditRequiredCertificates([]);
    setEditJobGradeId("");
  };

  const saveEdit = async () => {
    console.log('saveEdit called with full details:', {
      editingField,
      currentUser,
      hasCurrentUser: !!currentUser,
      hasUserId: !!currentUser?.id,
      editDuties,
      jobRole
    });

    if (!editingField || !currentUser?.id) {
      console.log('saveEdit early return:', {
        editingField,
        currentUser,
        hasCurrentUser: !!currentUser,
        hasUserId: !!currentUser?.id
      });
      return;
    }

    let updates: any = {};

    if (editingField === 'description') {
      updates.description = editValue as string;
    } else if (editingField === 'outcomes') {
      // Filter out empty outcomes
      const filteredOutcomes = editOutcomes.filter(o => o.trim() !== "");
      updates.outcomes = filteredOutcomes;
    } else if (editingField === 'keyDuties') {
      // Filter out empty duties
      const filteredDuties = editDuties.filter(d => d.trim() !== "");
      updates.keyDuties = filteredDuties;
    } else if (editingField === 'preferredQualifications') {
      updates.preferredQualifications = editPreferredQualifications;
    } else if (editingField === 'requiredCertificatesAndLicenses') {
      updates.requiredCertificatesAndLicenses = editRequiredCertificates;
    } else if (editingField === 'salaryRange') {
      updates.jobGradeId = editJobGradeId === "none" ? null : editJobGradeId;
    }

    // Activity log is handled by the backend now

    // Always include the department ID to prevent it from being lost
    if (department) {
      updates.department = department.id;
    }

    try {
      await updateJobRole(jobRole.id, updates);
      setEditingField(null);
      setEditValue("");
      setEditOutcomes([]);
      setEditDuties([]);
      setEditPreferredQualifications([]);
      setEditRequiredCertificates([]);
      setEditJobGradeId("");
    } catch (error) {
      console.error('Failed to save edit:', error);
    }
  };

  const handleArchive = async () => {
    if (!currentUser || !canArchive) return;

    try {
      const result = await archiveJobRole(jobRole.id, currentUser.id, archiveReason);

      if (result.success) {
        setOpenArchive(false);
        setArchiveReason("");
        // Update local state
        setJobRole({
          ...jobRole,
          archived: true,
          archivedOn: new Date().toISOString(),
          archivedBy: currentUser.id,
          archivedReason: archiveReason
        });
      } else if (result.affectedUsers && result.affectedUsers.length > 0) {
        // Display error with affected users
        const affectedUsersList = result.affectedUsers.map(user => user.name).join(", ");
        toast.error(`Cannot archive: Job role is currently assigned to users: ${affectedUsersList}`);
        setOpenArchive(false);
      } else {
        toast.error("Failed to archive job role");
        setOpenArchive(false);
      }
    } catch (error) {
      toast.error("Failed to archive job role");
      setOpenArchive(false);
    }
  };

  const handleUnarchive = async () => {
    if (!currentUser || !canArchive) return;

    try {
      await unarchiveJobRole(jobRole.id, currentUser.id);
      setOpenUnarchive(false);
      // Update local state
      setJobRole({
        ...jobRole,
        archived: false,
        archivedOn: undefined,
        archivedBy: undefined,
        archivedReason: undefined
      });
    } catch (error) {
      toast.error("Failed to unarchive job role");
      setOpenUnarchive(false);
    }
  };

  const addOutcome = () => {
    setEditOutcomes([...editOutcomes, ""]);
  };

  const addDuty = () => {
    setEditDuties([...editDuties, ""]);
  };

  const handleOutcomeChange = (index: number, value: string) => {
    const updated = [...editOutcomes];
    updated[index] = value;
    setEditOutcomes(updated);
  };

  const handleDutyChange = (index: number, value: string) => {
    console.log('handleDutyChange called:', { index, value, currentDuties: editDuties });
    const updated = [...editDuties];
    updated[index] = value;
    setEditDuties(updated);
    console.log('Updated duties:', updated);
  };

  const removeOutcome = (index: number) => {
    setEditOutcomes(editOutcomes.filter((_, i) => i !== index));
  };

  const removeDuty = (index: number) => {
    setEditDuties(editDuties.filter((_, i) => i !== index));
  };

  const getUserName = (userIdOrObj: any) => {
    // If userId is populated (object), use its name
    if (userIdOrObj && typeof userIdOrObj === "object" && userIdOrObj.name) {
      return userIdOrObj.name;
    }
    // Otherwise, try to find in users array
    const user = users.find(u => u.id === userIdOrObj);
    if (user) return user.name;
    // Fallback: if the userId matches the current user
    if (currentUser && currentUser.id === userIdOrObj) return currentUser.name;
    return "Unknown";
  };



  // Handle attachments change
  const handleAttachmentsChange = async (newAttachments: any[]) => {
    setAttachments(newAttachments);
    const response = await getJobRole(id);
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.role) {
        setJobRole(mapJobRole(data.role));
      }
    }
  };

  return (
    <Can I={ACTIONS.READ} a={RESOURCES.JOB_ROLE_DETAIL}
      passThrough={<AccessRestricted />} >
      <div className="space-y-4 md:space-y-6 p-2 md:p-0">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Link to="/job-roles">
              <Button variant="outline" size="icon" className="flex-shrink-0">
                <ArrowLeft size={16} />
              </Button>
            </Link>
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold truncate">{jobRole.title}</h1>
              <div className="flex gap-2">
                <Badge variant="outline" className="border-pink-200 bg-pink-50 text-pink-800">
                  Job Role
                </Badge>
                {jobRole.archived && (
                  <Badge variant="outline" className="ml-2 bg-yellow-50 text-yellow-800 border-yellow-200">
                    <Archive className="w-3 h-3 mr-1" /> Archived
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE}>
            <div className="w-full md:w-auto">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                {!jobRole.archived && (
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE}>
                    <Link to={`/job-roles/${jobRole.id}/edit`}>
                      <Button variant="outline"
                        className="flex items-center gap-2 w-full sm:w-auto">
                        <Edit className="w-4 h-4" />
                        <span>Edit</span>
                      </Button>
                    </Link>
                  </Can>
                )}
                {!jobRole.archived && (
                  <Can I={ACTIONS.ARCHIVE} a={RESOURCES.JOB_ROLE}>
                    <Dialog open={openArchive} onOpenChange={setOpenArchive}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="flex items-center gap-2 w-full sm:w-auto">
                          <Archive size={16} />
                          <span>Archive</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-lg">Archive Job Role</DialogTitle>
                          <DialogDescription className="text-sm">
                            Archiving will hide this job role from active use. It will remain for historical reference.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                          {users.some(user => user.jobRole === jobRole.title) ? (
                            <>
                              <p className="text-sm text-black">
                                This Job Role ({jobRole.title}) is being occupied by:
                              </p>
                              <ul className="list-disc pl-6 text-sm">
                                {users
                                  .filter(user => user.jobRole === jobRole.title)
                                  .map(user => (
                                    <li key={user.id}>{user.name}</li>
                                  ))}
                              </ul>
                              <p className="text-sm">Resolve this and return here to archive this job role.</p>
                            </>
                          ) : (
                            <>
                              <p className="text-sm">Are you sure you want to archive the job role "{jobRole.title}"?</p>
                              <div className="space-y-2">
                                <Label htmlFor="archiveReason" className="text-sm">Reason for Archiving (Optional)</Label>
                                <Textarea
                                  id="archiveReason"
                                  placeholder="Enter the reason for archiving this job role..."
                                  value={archiveReason}
                                  onChange={(e) => setArchiveReason(e.target.value)}
                                  rows={3}
                                  className="text-sm"
                                />
                              </div>
                            </>
                          )}
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                          </DialogClose>
                          <Button
                            variant="default"
                            className="gap-2"
                            onClick={handleArchive}
                            disabled={users.some(user => user.jobRole === jobRole.title)}
                          >
                            <Archive size={16} />
                            Archive Job Role
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </Can>
                )}
                {jobRole.archived && (
                  <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.JOB_ROLE}>
                    <Button variant="outline" onClick={handleUnarchive} className="flex items-center gap-2 w-full sm:w-auto">
                      <ArchiveRestore size={16} />
                      <span>Unarchive</span>
                    </Button>
                  </Can>
                )}
                {!jobRole.archived && (
                  <Can I={ACTIONS.DELETE} a={RESOURCES.JOB_ROLE}>
                    <Button variant="destructive" className="flex items-center gap-2 w-full sm:w-auto" onClick={() => setOpenDelete(true)}>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </Button>
                  </Can>
                )}
              </div>
            </div>
          </Can>
        </div>

        {/* Archive warning banner */}
        {jobRole.archived && (
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-yellow-800">This job role has been archived</h3>
                  <p className="text-sm text-yellow-700">
                    Archived on {formatArchiveDate(jobRole.archivedOn || "")} by {archiver ? archiver.name : "Unknown"}.
                    {jobRole.archivedReason && ` Reason: ${jobRole.archivedReason}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col gap-4">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'jobrole' | 'job_positions' | 'activity_log')} className="w-full">
            {/* <div className="w-full overflow-x-auto"> */}
            <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
              <TabsTrigger value="jobrole" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                Job Role
              </TabsTrigger>
              <TabsTrigger value="job_positions" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                Job Positions
              </TabsTrigger>
              <TabsTrigger value="activity_log" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                Activity Log
              </TabsTrigger>
            </TabsList>
            {/* </div> */}

            <TabsContent value="jobrole" className="space-y-6 mt-4">
              {/* Main content grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Left column on desktop, first on mobile */}
                <div className="md:col-span-1 space-y-6">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Job Role Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <dl className="space-y-4">
                        <div>
                          <dt className="text-sm text-muted-foreground">Job Title</dt>
                          <dd className="flex items-center gap-2"><Briefcase size={16} />{jobRole.title}</dd>
                        </div>
                        <div>
                          <dt className="text-sm text-muted-foreground">Department</dt>
                          <dd className="flex items-center gap-2">
                            <Building2 size={16} />
                            {department ? <Link to={`/departments/${department.id}`} className="text-primary hover:underline">{department.departmentCode} - {department.departmentName}</Link> : "Unknown"}
                          </dd>
                        </div>
                      </dl>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Current Positions</CardTitle>
                      <Button variant="outline" size="sm" onClick={() => {
                        if (jobRole) {
                          navigate(`/job-roles/${jobRole.id}/positions/new`);
                        }
                      }}>
                        <Plus size={14} className="mr-1" /> Add Position
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {positionsLoading ? (
                        <p className="text-sm text-muted-foreground italic">Loading positions...</p>
                      ) : positions.length > 0 ? (
                        <div className="flex flex-col gap-3">
                          {positions.map((position) => (
                            <div key={position.id} className="flex items-center gap-2 py-2 border-b last:border-0">
                              <Briefcase size={16} className="text-muted-foreground shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{position.positionCode}</p>
                                <p className="text-xs text-muted-foreground">{position.location}</p>
                              </div>
                              {position.status === 'Vacant' ? (
                                <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-none shrink-0">Vacant</Badge>
                              ) : (
                                <span className="text-sm text-muted-foreground shrink-0" title={position.assignedUser?.name || "Filled"}>
                                  Occupied by {position.assignedUser?.name || "Filled"}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center space-y-3 py-6">
                          <div className="bg-gray-100 p-4 rounded-full">
                            <Briefcase className="h-8 w-8 text-gray-400" />
                          </div>
                          <div className="space-y-1 text-center">
                            <p className="font-medium">No positions found</p>
                            <p className="text-sm text-muted-foreground">Add a job position to get started.</p>
                          </div>
                          <Button variant="outline" size="sm" className="mt-2" onClick={() => {
                            if (jobRole) {
                              navigate(`/job-roles/${jobRole.id}/positions/new`);
                            }
                          }}>
                            <Plus size={16} className="mr-2" /> Add Position
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Right column on desktop, second on mobile */}
                <div className="md:col-span-2 space-y-6">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Description</CardTitle>
                      {!jobRole.archived && (
                        <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE_DESCRIPTION}>
                          <Button variant="outline" size="sm" onClick={() => startEdit('description', jobRole.description)} disabled={editingField === 'description'}>
                            <Edit size={16} className="mr-2" /> Edit
                          </Button>
                        </Can>
                      )}
                    </CardHeader>
                    <CardContent>
                      {editingField === 'description' ? (
                        <div className="space-y-4">
                          <Textarea value={editValue as string} onChange={(e) => setEditValue(e.target.value)} rows={5} />
                          <div className="flex justify-end space-x-2"><Button variant="outline" onClick={cancelEdit}>Cancel</Button><Button onClick={saveEdit}>Save</Button></div>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap text-sm">{jobRole.description || "No description provided."}</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle>Required Certificates & Licenses</CardTitle>
                      {!jobRole.archived && (
                        <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE}>
                          <Button variant="outline" size="sm" onClick={() => startEdit('requiredCertificatesAndLicenses', [])} disabled={editingField === 'requiredCertificatesAndLicenses'}>
                            <Edit size={16} className="mr-2" /> Edit
                          </Button>
                        </Can>
                      )}
                    </CardHeader>
                    <CardContent>
                      {editingField === 'requiredCertificatesAndLicenses' ? (
                        <div className="space-y-4">
                          <MultiSelect
                            options={qualificationPresets.map(p => ({ label: p.name, value: p.id }))}
                            onChange={(values: string[]) => setEditRequiredCertificates(values)}
                            selected={editRequiredCertificates}
                            placeholder="Select required certificates"
                            maxDisplayTags={3}
                          />
                          <div className="flex justify-end space-x-2"><Button variant="outline" onClick={cancelEdit}>Cancel</Button><Button onClick={saveEdit}>Save</Button></div>
                        </div>
                      ) : (
                        jobRole.requiredCertificatesAndLicenses && jobRole.requiredCertificatesAndLicenses.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {jobRole.requiredCertificatesAndLicenses.map((cert) => (
                              <Badge key={cert.id} variant="secondary" className="px-2 py-1 flex items-center gap-1.5">
                                {cert.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground italic text-sm">No specific certifications required for this role.</p>
                        )
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle>Preferred Qualifications</CardTitle>
                      {!jobRole.archived && (
                        <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE}>
                          <Button variant="outline" size="sm" onClick={() => startEdit('preferredQualifications', [])} disabled={editingField === 'preferredQualifications'}>
                            <Edit size={16} className="mr-2" /> Edit
                          </Button>
                        </Can>
                      )}
                    </CardHeader>
                    <CardContent>
                      {editingField === 'preferredQualifications' ? (
                        <div className="space-y-4">
                          <MultiSelect
                            options={qualificationPresets.map(p => ({ label: p.name, value: p.id }))}
                            onChange={(values: string[]) => setEditPreferredQualifications(values)}
                            selected={editPreferredQualifications}
                            placeholder="Select preferred qualifications"
                            maxDisplayTags={3}
                          />
                          <div className="flex justify-end space-x-2"><Button variant="outline" onClick={cancelEdit}>Cancel</Button><Button onClick={saveEdit}>Save</Button></div>
                        </div>
                      ) : (
                        jobRole.preferredQualifications && jobRole.preferredQualifications.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {jobRole.preferredQualifications.map((qual) => (
                              <Badge key={qual.id} variant="secondary" className="px-2 py-1 flex items-center gap-1.5">
                                {qual.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground italic text-sm">No preferred qualifications defined for this role.</p>
                        )
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle>Grading Scale</CardTitle>
                      {!jobRole.archived && (
                        <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE}>
                          <Button variant="outline" size="sm" onClick={() => startEdit('salaryRange', '')} disabled={editingField === 'salaryRange'}>
                            <Edit size={16} className="mr-2" /> Edit
                          </Button>
                        </Can>
                      )}
                    </CardHeader>
                    <CardContent>
                      {editingField === 'salaryRange' ? (
                        <div className="space-y-3">
                          <Select value={editJobGradeId} onValueChange={(val) => setEditJobGradeId(val)}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select job grade" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              {jobGrades.filter(g => !g.deleted).map((grade) => (
                                <SelectItem key={grade.id} value={grade.id}>{grade.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex justify-end space-x-2">
                            <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
                            <Button onClick={saveEdit}>Save</Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {jobRole.jobGrade ? (
                            <div className="space-y-2">
                              <p className="text-sm font-bold text-gray-900">{jobRole.jobGrade.name}</p>
                              <div className="flex items-center gap-6">
                                <div>
                                  <p className="text-sm text-muted-foreground">Minimum</p>
                                  <p className="text-sm font-medium">{jobRole.jobGrade.salaryRangeMin !== undefined && jobRole.jobGrade.salaryRangeMin !== null ? `$${jobRole.jobGrade.salaryRangeMin}` : "Not specified"}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Maximum</p>
                                  <p className="text-sm font-medium">{jobRole.jobGrade.salaryRangeMax !== undefined && jobRole.jobGrade.salaryRangeMax !== null ? `$${jobRole.jobGrade.salaryRangeMax}` : "Not specified"}</p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">Not specified</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Full-width cards for Outcomes, Duties, and Attachments */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Outcomes</CardTitle>
                  {!jobRole.archived && (
                    <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE_OUTCOMES}>
                      <Button variant="outline" size="sm" onClick={() => startEdit('outcomes', jobRole.outcomes)} disabled={editingField === 'outcomes'}>
                        <Edit size={16} className="mr-2" /> Edit
                      </Button>
                    </Can>
                  )}
                </CardHeader>
                <CardContent>
                  {editingField === 'outcomes' ? (
                    <div className="space-y-4">
                      {editOutcomes.map((outcome, index) => (
                        <div key={index} className="flex gap-2"><Input value={outcome} onChange={(e) => handleOutcomeChange(index, e.target.value)} /><Button variant="ghost" size="icon" onClick={() => removeOutcome(index)} className="text-destructive"><Trash2 size={16} /></Button></div>
                      ))}
                      <Button variant="outline" onClick={addOutcome} size="sm"><Plus size={16} className="mr-2" /> Add Outcome</Button>
                      <div className="flex justify-end space-x-2 mt-4"><Button variant="outline" onClick={cancelEdit}>Cancel</Button><Button onClick={saveEdit}>Save</Button></div>
                    </div>
                  ) : (
                    <ol className="list-decimal pl-5 space-y-2 text-sm">
                      {jobRole.outcomes?.length > 0 ? jobRole.outcomes.map((o, i) => <li key={i}>{o}</li>) : <p className="text-muted-foreground italic">No outcomes defined.</p>}
                    </ol>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Key Duties</CardTitle>
                  {!jobRole.archived && (
                    <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE_DUTIES}>
                      <Button variant="outline" size="sm" onClick={() => startEdit('keyDuties', jobRole.keyDuties)} disabled={editingField === 'keyDuties'}>
                        <Edit size={16} className="mr-2" /> Edit
                      </Button>
                    </Can>
                  )}
                </CardHeader>
                <CardContent>
                  {editingField === 'keyDuties' ? (
                    <div className="space-y-4">
                      {editDuties.map((duty, index) => (
                        <div key={index} className="flex gap-2"><Input value={duty} onChange={(e) => handleDutyChange(index, e.target.value)} /><Button variant="ghost" size="icon" onClick={() => removeDuty(index)} className="text-destructive"><Trash2 size={16} /></Button></div>
                      ))}
                      <Button variant="outline" onClick={addDuty} size="sm"><Plus size={16} className="mr-2" /> Add Duty</Button>
                      <div className="flex justify-end space-x-2 mt-4"><Button variant="outline" onClick={cancelEdit}>Cancel</Button><Button onClick={saveEdit}>Save</Button></div>
                    </div>
                  ) : (
                    <ol className="list-decimal pl-5 space-y-2 text-sm">
                      {jobRole.keyDuties?.length > 0 ? jobRole.keyDuties.map((d, i) => <li key={i}>{d}</li>) : <p className="text-muted-foreground italic">No key duties defined.</p>}
                    </ol>
                  )}
                </CardContent>
              </Card>

              <FileAttachments
                resourceType="jobroles"
                resourceId={jobRole.id}
                attachments={attachments}
                onAttachmentsChange={handleAttachmentsChange}
                // canEdit={canArchive || false} 
                disabled={jobRole.archived} />
            </TabsContent>
            
            <TabsContent value="job_positions" className="space-y-6 mt-4">
              <JobPositionsTab
                jobRole={jobRole}
                isActive={activeTab === 'job_positions'}
                shouldOpenForm={shouldOpenPositionForm}
                onFormOpened={() => setShouldOpenPositionForm(false)}
                onPositionsChange={fetchPositions}
              />
            </TabsContent>

            <TabsContent value="activity_log" className="space-y-6 mt-4">
              <ActivityLog
                logs={jobRole.activityLogs || []}
                entityName="Job Role"
                users={users}
                currentUser={currentUser}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Delete Job Role Dialog */}
        <Dialog open={openDelete} onOpenChange={setOpenDelete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-lg">Delete Job Role</DialogTitle>
              <DialogDescription className="text-sm">
                This will permanently remove the job role and cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {jobRole?.title && (
              <div className="py-4 space-y-4">
                {users.some(user => user.jobRole === jobRole.title) ? (
                  <>
                    <p className="text-sm text-black">
                      This Job Role ({jobRole.title}) is being occupied by:
                    </p>
                    <ul className="list-disc pl-6 text-sm">
                      {users
                        .filter(user => user.jobRole === jobRole.title)
                        .map(user => (
                          <li key={user.id}>{user.name}</li>
                        ))}
                    </ul>
                    <p className="text-sm">Resolve this and return here to delete this job role.</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm">Are you sure you want to delete the job role "{jobRole.title}"?</p>
                    <p className="text-sm text-red-600">This action cannot be undone.</p>
                  </>
                )}
              </div>
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                onClick={async () => {
                  // Call deleteJobRole from context
                  if (!jobRole) return;
                  if (users.some(user => user.jobRole === jobRole.title)) return;
                  await deleteJobRole(jobRole.id);
                  setOpenDelete(false);
                  navigate('/job-roles');
                }}
                disabled={users.some(user => user.jobRole === jobRole.title)}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Can>
  )
}
